export type TransportMode = 'LTL' | 'FTL' | 'OCEAN' | 'AIR'
export type WeightUnit = 'lbs' | 'kg'
export type CargoType = 'Standard' | 'Hazmat' | 'Refrigerated'
export type OceanContainer = 'FCL' | 'LCL'

export interface CalcInput {
  mode: TransportMode
  origin: string
  destination: string
  weight: number
  weightUnit: WeightUnit
  length: number
  width: number
  height: number
  dimUnit: 'in' | 'cm'
  cargoType: CargoType
  oceanContainer: OceanContainer
}

export interface RateLine {
  label: string
  amount: number
}

export interface RateResult {
  total: number
  lines: RateLine[]
  note: string
}

const KG_TO_LBS = 2.20462
const IN_TO_CM = 2.54
const HAZMAT_FEE = 75

function toLbs(weight: number, unit: WeightUnit): number {
  return unit === 'kg' ? weight * KG_TO_LBS : weight
}

// Convert dimensions to inches for imperial-based formulas
function toInches(v: number, unit: 'in' | 'cm'): number {
  return unit === 'cm' ? v / IN_TO_CM : v
}

// Mock distance estimation: deterministic pseudo-distance from strings, default 500
function estimateMiles(origin: string, destination: string): number {
  const o = origin.trim()
  const d = destination.trim()
  if (!o || !d) return 500
  let seed = 0
  for (const ch of (o + d)) seed = (seed * 31 + ch.charCodeAt(0)) % 100000
  // Range roughly 250 - 1750 miles
  return 250 + (seed % 1500)
}

export function calculateFreight(input: CalcInput): RateResult {
  const lbs = toLbs(input.weight || 0, input.weightUnit)
  const lengthIn = toInches(input.length || 0, input.dimUnit)
  const widthIn = toInches(input.width || 0, input.dimUnit)
  const heightIn = toInches(input.height || 0, input.dimUnit)

  const lines: RateLine[] = []
  let total = 0
  let note = ''

  switch (input.mode) {
    case 'LTL': {
      const base = 150
      const weightCost = lbs * 0.35
      lines.push({ label: 'Base rate', amount: base })
      lines.push({ label: `Weight (${Math.round(lbs)} lbs x $0.35)`, amount: weightCost })
      if (input.cargoType === 'Hazmat') {
        lines.push({ label: 'Hazmat surcharge', amount: HAZMAT_FEE })
      }
      total = lines.reduce((s, l) => s + l.amount, 0)
      note = 'Less-than-truckload estimate based on weight and accessorials.'
      break
    }
    case 'FTL': {
      const miles = estimateMiles(input.origin, input.destination)
      const lineHaul = miles * 2.5
      lines.push({ label: `Line haul (${miles} mi x $2.50)`, amount: lineHaul })
      if (input.cargoType === 'Hazmat') {
        lines.push({ label: 'Hazmat surcharge', amount: HAZMAT_FEE })
      }
      total = lines.reduce((s, l) => s + l.amount, 0)
      note = `Full truckload estimated over ~${miles} miles at $2.50/mile.`
      break
    }
    case 'OCEAN': {
      if (input.oceanContainer === 'FCL') {
        lines.push({ label: 'FCL flat container rate', amount: 3200 })
        total = 3200
        note = 'Flat rate per standard 20ft / 40ft container (FCL).'
      } else {
        // LCL: $120 per CBM. Volume in cubic meters from inches.
        const cubicInches = lengthIn * widthIn * heightIn
        const cbm = cubicInches / 61023.7
        const volCost = Math.max(cbm, 1) * 120
        lines.push({ label: `Volume (${cbm.toFixed(2)} CBM x $120)`, amount: volCost })
        total = volCost
        note = 'Less-than-container-load billed per cubic meter (min 1 CBM).'
      }
      break
    }
    case 'AIR': {
      const dimWeight = (lengthIn * widthIn * heightIn) / 166
      const chargeable = Math.max(dimWeight, lbs)
      const cost = chargeable * 4.5
      lines.push({ label: `Chargeable weight (${Math.round(chargeable)} lbs)`, amount: cost })
      total = cost
      note = 'Air freight uses the greater of dimensional or actual weight at $4.50/lb.'
      break
    }
  }

  return { total: Math.round(total * 100) / 100, lines, note }
}

export const MODE_LABELS: Record<TransportMode, string> = {
  LTL: 'LTL / Truck Freight',
  FTL: 'Full Truckload (FTL)',
  OCEAN: 'Ocean Container (FCL/LCL)',
  AIR: 'Air Freight',
}

export function formatUSD(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}
